import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, todayISO } from "@/lib/api";
import { toast } from "sonner";

const initialForm = () => ({
  worker_id: "",
  work_type: "",
  description: "",
  quantity_given: "",
  given_date: todayISO(),
  notes: "",
});

export default function AddWorkDialog({ open, onOpenChange, workers, onSuccess, defaultWorkerId }) {
  const [form, setForm] = useState(initialForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ ...initialForm(), worker_id: defaultWorkerId || (workers[0]?.id || "") });
    }
  }, [open, defaultWorkerId, workers]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.worker_id || !form.work_type || !form.quantity_given) {
      toast.error("Çalışan, iş türü ve adet zorunludur");
      return;
    }
    setSaving(true);
    try {
      await api.post("/works", {
        ...form,
        quantity_given: parseInt(form.quantity_given, 10),
      });
      toast.success("İş kaydedildi");
      onOpenChange(false);
      onSuccess?.();
    } catch {
      toast.error("Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-lg" data-testid="add-work-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Yeni İş Ekle</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-semibold tracking-widest uppercase text-[#5C605C]">Çalışan</label>
            <Select value={form.worker_id} onValueChange={(v) => setForm({ ...form, worker_id: v })}>
              <SelectTrigger className="mt-1.5 h-12 rounded-xl border-[#E6E6E2]" data-testid="add-work-worker-select">
                <SelectValue placeholder="Çalışan seçin" />
              </SelectTrigger>
              <SelectContent>
                {workers.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold tracking-widest uppercase text-[#5C605C]">İş Türü</label>
              <Input
                value={form.work_type}
                onChange={(e) => setForm({ ...form, work_type: e.target.value })}
                placeholder="Ör: Gömlek ütü"
                className="mt-1.5 h-12 rounded-xl border-[#E6E6E2] focus-visible:ring-[#3B5249]/25"
                data-testid="add-work-type-input"
              />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-widest uppercase text-[#5C605C]">Adet</label>
              <Input
                type="number"
                min="1"
                value={form.quantity_given}
                onChange={(e) => setForm({ ...form, quantity_given: e.target.value })}
                placeholder="Ör: 50"
                className="mt-1.5 h-12 rounded-xl border-[#E6E6E2] focus-visible:ring-[#3B5249]/25"
                data-testid="add-work-quantity-input"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold tracking-widest uppercase text-[#5C605C]">Verilme Tarihi</label>
            <Input
              type="date"
              value={form.given_date}
              onChange={(e) => setForm({ ...form, given_date: e.target.value })}
              className="mt-1.5 h-12 rounded-xl border-[#E6E6E2] focus-visible:ring-[#3B5249]/25"
              data-testid="add-work-date-input"
            />
          </div>

          <div>
            <label className="text-xs font-semibold tracking-widest uppercase text-[#5C605C]">Açıklama (opsiyonel)</label>
            <Input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Ör: Beyaz, mavi karışık"
              className="mt-1.5 h-12 rounded-xl border-[#E6E6E2] focus-visible:ring-[#3B5249]/25"
              data-testid="add-work-desc-input"
            />
          </div>

          <div>
            <label className="text-xs font-semibold tracking-widest uppercase text-[#5C605C]">Notlar (opsiyonel)</label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Ek notlar..."
              rows={3}
              className="mt-1.5 rounded-xl border-[#E6E6E2] focus-visible:ring-[#3B5249]/25"
              data-testid="add-work-notes-input"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="rounded-full h-11 px-5 transition-colors"
              data-testid="add-work-cancel"
            >
              İptal
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="rounded-full h-11 px-6 bg-[#3B5249] hover:bg-[#2C3E37] text-white transition-colors"
              data-testid="add-work-submit"
            >
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
